import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import type { Notification } from '../api/types';
import { usePaginated } from '../lib/hooks';
import { useLang } from '../lib/useLang';
import { formatDateTime } from '../lib/format';
import { PageHeader } from '../components/PageHeader';
import { ApiState, EmptyState } from '../components/States';
import { LoadMore } from '../components/Pagination';
import { Badge, Button, Card } from '../components/ui';
import { IconBell, IconInfo, IconCheck, IconWarning, IconError } from '../components/Icons';

const PAGE = 30;

function kindIcon(kind: string) {
  switch (kind) {
    case 'success':
      return <IconCheck width={18} height={18} style={{ color: 'var(--success)' }} />;
    case 'warning':
      return <IconWarning width={18} height={18} style={{ color: 'var(--warning)' }} />;
    case 'error':
      return <IconError width={18} height={18} style={{ color: 'var(--danger)' }} />;
    default:
      return <IconInfo width={18} height={18} style={{ color: 'var(--info)' }} />;
  }
}

export function Notifications() {
  const { t } = useTranslation();
  const lang = useLang();
  const list = usePaginated<Notification>((cursor) => api.listNotifications(PAGE, cursor), []);
  // Local read-state overlay (the contract exposes no mark-read endpoint yet).
  const [readLocal, setReadLocal] = useState<Set<string>>(new Set());
  useEffect(() => setReadLocal(new Set()), [list.status]);

  const markAll = () => setReadLocal(new Set(list.items.map((n) => n.id)));
  const unread = list.items.filter((n) => !n.read && !readLocal.has(n.id)).length;

  return (
    <>
      <PageHeader
        title={t('notifications.title')}
        subtitle={t('notifications.subtitle')}
        actions={
          list.items.length > 0 ? (
            <Button variant="secondary" onClick={markAll} disabled={unread === 0}>
              {t('notifications.markAll')}
            </Button>
          ) : undefined
        }
      />
      <Card>
        <ApiState status={list.status} error={list.error} data={list.status === 'error' ? null : list.items} onRetry={list.reload}>
          {(items) =>
            items.length === 0 ? (
              <EmptyState title={t('notifications.empty')} icon={<IconBell width={26} height={26} />} />
            ) : (
              <>
                {unread > 0 ? (
                  <div className="card__head">
                    <Badge tone="accent">
                      {unread} {t('notifications.unread')}
                    </Badge>
                  </div>
                ) : null}
                <div className="card__body">
                  {items.map((n) => {
                    const isRead = n.read || readLocal.has(n.id);
                    return (
                      <div className="upload-item" key={n.id} style={{ opacity: isRead ? 0.65 : 1 }}>
                        <span>{kindIcon(n.kind)}</span>
                        <div className="upload-item__info">
                          <div className={isRead ? 'upload-item__name' : 'upload-item__name cell-strong'}>{n.message}</div>
                          <div className="upload-item__meta">{formatDateTime(n.at, lang)}</div>
                        </div>
                        {!isRead ? <span className="tenant-chip__dot" aria-label={t('notifications.unread')} /> : null}
                      </div>
                    );
                  })}
                </div>
                <LoadMore hasMore={list.hasMore} loading={list.loadingMore} onLoadMore={list.loadMore} />
              </>
            )
          }
        </ApiState>
      </Card>
    </>
  );
}
